import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';
import { DepartmentVisibilities } from 'src/app/modules/shared/enums/departmentVisibility.enum';
import { EmployeeRoleTypes } from 'src/app/modules/shared/enums/employees/EmployeeRoleTypes';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { EmployeeSettingsService } from 'src/app/modules/shared/services/employee-settings.service';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { Account } from 'src/app/modules/shared/models/account';
import { Employee } from 'src/app/modules/shared/models/employee';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { Subscription, forkJoin } from 'rxjs';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { T } from 'src/assets/i18n/translation-keys';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import { PermissionViewModel } from 'src/app/modules/shared/viewModels/permissionViewModel';
import { PermissionHelper } from 'src/app/modules/settings/utils/permission.helper';

enum ReadWriteTypes {
  read = 'read',
  write = 'write',
}

@Component({
    selector: 'app-risk-employee-permissions',
    templateUrl: './risk-employee-permissions.component.html',
    styleUrls: ['./risk-employee-permissions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RiskEmployeePermissionsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('canEdit') canEdit: TemplateRef<ElementRef<HTMLElement>>;
  @ViewChild('canSee') canSee: TemplateRef<ElementRef<HTMLElement>>;
  @ViewChild('isAdmin') isAdmin: TemplateRef<ElementRef<HTMLElement>>;
  @Input() employeeDetailsViewModel: EmployeeDetailsViewModel;
  @Input() isInPrimaryRole: boolean;
  @Input() isAccountAdmin: boolean;
  @Input() isDepartmentSelected: boolean;

  public childAccounts: Account[];

  public readRisksNonConfidentialDepVisibility: DepartmentVisibilities;
  public writeRisksNonConfidentialDepVisibility: DepartmentVisibilities;

  public readRAsNonConfidentialDepVisibility: DepartmentVisibilities;
  public writeRAsNonConfidentialDepVisibility: DepartmentVisibilities;

  public readonly employeeRoleTypes = EmployeeRoleTypes;
  public readonly filterTypes = FilterTypes;
  public readonly departmentVisibilites = DepartmentVisibilities;
  public readonly moduleTypes = ModuleTypes;
  public readonly readWriteTypes = ReadWriteTypes;
  public isDropdownLoading = false;
  public readonly T = T;

  public account: Account;
  private employee: Employee;
  private subscriptions = new Subscription();
  private allowedFilters: FilterViewModel[] = [];

  localisedEmployee: string = 'Employee';
  localisedEmployees: string = 'Employees';
  localisedTasks: string = 'Tasks';
  localisedDepartment: string = 'Department';
  localisedDepartments: string = 'Departments';
  localisedProject: string = 'Project';
  localisedProjects: string = 'Projects';
  objectTypes = ObjectTypes;

  constructor(
    private readonly accountSettingsService: AccountSettingsService,
    private readonly authenticationService: AuthenticationService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly localisationService: LocalisationService,
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.account = this.authenticationService.getCurrentAccount();
    this.initDepartmentVisibilities();
    this.initDepartmentVisibilities(FilterTypes.Risk_Assesment);
    this.isDropdownLoading = true;

    this.subscriptions.add(
      forkJoin([
        this.employeeSettingsService.getConfidentialIDs(ObjectTypes.Project),
        this.accountSettingsService.getChildAccounts(),
      ]).subscribe((res: any) => {
        this.childAccounts = res[1];
        this.isDropdownLoading = false;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.initLocalisations();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges() {
    this.initDepartmentVisibilities();
  }

  initLocalisations() {
    this.localisedEmployee = this.localisationService.localiseObjectType(ObjectTypes.Employee);
    this.localisedEmployees = this.localisationService.localiseObjectType(ObjectTypes.Employee, true);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);
    this.localisedDepartments = this.localisationService.localiseObjectType(ObjectTypes.Department, true);
    this.localisedProject = this.localisationService.localiseObjectType(ObjectTypes.Project);
    this.localisedProjects = this.localisationService.localiseObjectType(ObjectTypes.Project, true);
    this.localisedTasks = this.localisationService.localiseObjectType(ObjectTypes.Task, true);
    this.changeDetectorRef.markForCheck();
  }

  hasAccessToObject(objectType: ObjectTypes) {
    return EmployeeUtil.hasAccessToObjectType(this.employeeDetailsViewModel, objectType);
  }

  isInRole(employeeRole: EmployeeRoleTypes) {
    return EmployeeUtil.hasRole(this.employeeDetailsViewModel, employeeRole);
  }

  selectRiskUserRole(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Risk,
        objectType: ObjectTypes.Risk,
        created: '',
        accountId: this.employee.accountId,
        createdById: this.employee.id
      });
      this.readRisksNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeRisksNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(FilterTypes.Risk, 0, this.readRisksNonConfidentialDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          FilterTypes.Risk,
          0,
          this.writeRisksNonConfidentialDepVisibility,
          ReadWriteTypes.write,
          false
        )
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => p.module !== ModuleTypes.Risk
      );

      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter((r) => r.objectType !== ObjectTypes.Risk);
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId !== EmployeeRoleTypes.Risk_Manager
      );
    }
    this.initDepartmentVisibilities();
    this.changeDetectorRef.detectChanges();
  }

  selectRiskAssessmentUserRole(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.modulePermissionViewModels.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        module: ModuleTypes.Risk,
        objectType: ObjectTypes.Risk_Assesment,
        createdById: this.employee.id,
        created: '',
        accountId: this.employee.accountId,
      });
      this.readRAsNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeRAsNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(FilterTypes.Risk_Assesment, 0, this.readRAsNonConfidentialDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          FilterTypes.Risk_Assesment,
          0,
          this.writeRAsNonConfidentialDepVisibility,
          ReadWriteTypes.write,
          false
        )
      );
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(
        (p) => p.module !== ModuleTypes.Risk && p.type !== FilterTypes.Risk_Assesment
      );

      this.employeeDetailsViewModel.modulePermissionViewModels = this.employeeDetailsViewModel.modulePermissionViewModels.filter(
        (r) => r.objectType !== ObjectTypes.Risk_Assesment
      );
    }
    this.initDepartmentVisibilities(FilterTypes.Risk_Assesment);
    this.changeDetectorRef.detectChanges();
  }

  selectedRiskManager(isAdd: boolean) {
    if (isAdd) {
      this.employeeDetailsViewModel.employeeRole.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        roleId: EmployeeRoleTypes.Risk_Manager,
        created: '',
        accountId: 0,
      });
      this.readRisksNonConfidentialDepVisibility = DepartmentVisibilities.All_Departments;
      this.writeRisksNonConfidentialDepVisibility = DepartmentVisibilities.Nothing;
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(FilterTypes.Risk, 0, this.readRisksNonConfidentialDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          FilterTypes.Risk,
          0,
          this.writeRisksNonConfidentialDepVisibility,
          ReadWriteTypes.write,
          false
        )
      );

      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(FilterTypes.Risk_Assesment, 0, this.readRAsNonConfidentialDepVisibility, ReadWriteTypes.read, false)
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          FilterTypes.Risk_Assesment,
          0,
          this.writeRAsNonConfidentialDepVisibility,
          ReadWriteTypes.write,
          false
        )
      );
    } else {
      this.employeeDetailsViewModel.employeeRole = this.employeeDetailsViewModel.employeeRole.filter(
        (r) => r.roleId != EmployeeRoleTypes.Risk_Manager
      );
      this.changeDepartmentVisibility(DepartmentVisibilities.All_Departments, FilterTypes.Risk, ReadWriteTypes.read, false);
      this.changeDepartmentVisibility(DepartmentVisibilities.Nothing, FilterTypes.Risk, ReadWriteTypes.write, false);
      this.changeDepartmentVisibility(DepartmentVisibilities.All_Departments, FilterTypes.Risk_Assesment, ReadWriteTypes.read, false);
      this.changeDepartmentVisibility(DepartmentVisibilities.Nothing, FilterTypes.Risk_Assesment, ReadWriteTypes.write, false);
    }
  }

  generatePermission(
    filterType: FilterTypes,
    value: number,
    departmentVisibility: DepartmentVisibilities,
    readOrWrite: ReadWriteTypes,
    confidential: boolean,
    accountId: number = 0
  ) {
    return PermissionHelper.generatePermission(this.employeeDetailsViewModel.id, this.employee.accountId, this.employee.id, ModuleTypes.Risk, filterType, value, departmentVisibility, readOrWrite, confidential, accountId);
  }

  private initDepartmentVisibilities(filterType: FilterTypes = FilterTypes.Risk) {
    if(filterType === FilterTypes.Risk) {
      this.readRisksNonConfidentialDepVisibility = this.getDepartmentVisibility(
        ReadWriteTypes.read,
        filterType
      );
      this.writeRisksNonConfidentialDepVisibility = this.getDepartmentVisibility(
        ReadWriteTypes.read,
        filterType
      );
    }
    else if(filterType === FilterTypes.Risk_Assesment) {
      this.readRAsNonConfidentialDepVisibility = this.getDepartmentVisibility(
        ReadWriteTypes.read,
        filterType,
      );
      this.writeRAsNonConfidentialDepVisibility = this.getDepartmentVisibility(
        ReadWriteTypes.write,
        filterType
      );
    }
  }

  getDepartmentVisibility(readOrWrite: ReadWriteTypes, filterType: FilterTypes, confidential = false) {
    return PermissionHelper.getDepartmentVisibility(this.employeeDetailsViewModel, readOrWrite, filterType, ModuleTypes.Risk, confidential);
  }

  changeDepartmentVisibility(
    currentDepVisibility: DepartmentVisibilities,
    filterType: FilterTypes,
    readOrWrite: ReadWriteTypes,
    confidential = false
  ) {

    PermissionHelper.changeDepartmentVisibility(this.employeeDetailsViewModel, currentDepVisibility,ModuleTypes.Risk, filterType, readOrWrite, confidential);
    this.initDepartmentVisibilities();
  }

  canSeeConfidentialRAs() {
    return this.employeeDetailsViewModel.permissions.find(p => p.module === ModuleTypes.Risk && p.type === FilterTypes.Risk_Assesment && p.confidential && p.read) !== undefined;
  }

  changeConfidentialRAAccess(checked: boolean) {
    if(checked){
      this.employeeDetailsViewModel.permissions.push(this.generatePermission(FilterTypes.Risk_Assesment, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.read, true));
      this.employeeDetailsViewModel.permissions.push(this.generatePermission(FilterTypes.Risk_Assesment, 0, DepartmentVisibilities.All_Departments, ReadWriteTypes.write, true));
    } else {
      this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions.filter(p => p.module !== ModuleTypes.Risk && p.type !== FilterTypes.Risk_Assesment && !p.confidential);
    }
  }

  updateEmployeePermissions(permissions: PermissionViewModel[]) {
    this.employeeDetailsViewModel.permissions = permissions.slice();
    this.changeDetectorRef.markForCheck();
  }
}
